import React, { useCallback } from 'react';
import { getContinueUrlWithWelcomeModal } from '@jira/project-settings__common/src/isValidNthUserWelcomePath.tsx';
import type { OnSubmitData, FeaturedUser } from '@atlassian/invite-people/types';
import { useFlagsService } from '@atlassian/jira-flags';
import ErrorFlag from '@atlassian/jira-flags/src/common/ui/components/error-flag';
import SuccessFlag from '@atlassian/jira-flags/src/common/ui/components/success-flag';
import WarningFlag from '@atlassian/jira-flags/src/common/ui/components/warning-flag';
import { projectTypeToSubProduct } from '@atlassian/jira-invite-people/src/ui/content/utils.tsx';
import { useProjectSidebarNavigation } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/common/project-sidebar-navigation-resource/index.tsx';
import { useOrgId } from '@atlassian/jira-router-resources-navigation-org-id/src/index.tsx';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import type { Flag } from '@atlassian/people-teams';
import { useCurrentUser } from '@atlassian/jira-platform-services-user-current/src/main.tsx';
import { useService } from '@atlassian/jira-expansion-services/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import {
	fetch3PInviteRecommendations,
	mapToFeaturedUser,
	type Recommendations3PInput,
	type UserInfo,
	type MapToFeaturedUserInput,
} from '@atlassian/expansion-services/services';
import { useIntl } from '@atlassian/jira-intl';
import { useInvitePeopleModal as useInvitePeopleModalInner } from '../controllers/use-invite-people-modal/index.tsx';
import messages from './messages.tsx';

export const useInvitePeopleModal = () => {
	const { showFlag } = useFlagsService();
	const { data: projectSidebarNavigationData } = useProjectSidebarNavigation();
	const cloudId = useCloudId();
	const { data: orgId } = useOrgId();
	const { software } = useAppEditions();

	const { data: currentUser } = useCurrentUser();
	const { formatMessage } = useIntl();

	let fetchInviteRecommendationsService = useCallback(
		(_: Recommendations3PInput) => Promise.resolve<UserInfo[]>([]),
		[],
	);
	let mapToFeaturedUserService: (input: MapToFeaturedUserInput) => FeaturedUser[] = useCallback(
		(_: MapToFeaturedUserInput) => [],
		[],
	);

	if (fg('tepi-3p-core-invites-slack-rec-ux')) {
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		fetchInviteRecommendationsService = useService(fetch3PInviteRecommendations);
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		mapToFeaturedUserService = useService(mapToFeaturedUser);
	}
	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	let continueUrl = window.location.href;

	if (currentUser?.user?.accountId) {
		const url = new URL(continueUrl);
		url.searchParams.append('inviterId', currentUser.user.accountId);
		continueUrl = url.toString();
	}

	const subProduct = projectTypeToSubProduct(projectSidebarNavigationData?.projectType);

	if (subProduct === 'software') {
		continueUrl = getContinueUrlWithWelcomeModal(continueUrl);
	}

	const fetchInviteRecommendations = useCallback(async () => {
		if (fg('tepi-3p-core-invites-slack-rec-ux')) {
			const inviteRecommendationsFromSlack = await fetchInviteRecommendationsService({
				limit: 10,
			}).then((recommendations) => {
				return mapToFeaturedUserService({
					userInfo: recommendations,
					tooltipRenderer: (user: UserInfo) =>
						formatMessage(messages.inviteRecommendationsFromSlack, { email: user.email }),
				});
			});

			return inviteRecommendationsFromSlack;
		}
		return [];
	}, [fetchInviteRecommendationsService, mapToFeaturedUserService, formatMessage]);

	const addFlag = useCallback(
		({ appearance, ...flagProps }: Flag) => {
			const Flag = getFlagComponent(appearance);
			showFlag((serviceProps) => (
				<Flag
					{...flagProps}
					{...serviceProps}
					messageId={`navigation-apps-sidebar-nav4-sidebars-content-teams.ui.flag.showFlag.${appearance}.${flagProps.id}`}
					messageType="transactional"
				/>
			));
		},
		[showFlag],
	);

	const { openModal: invitePeopleModalTrigger } = useInvitePeopleModalInner({
		continueUrl,
		enableThirdParty: true,
		displayInfiSpotlight: true,
		orgId,
		resourceAri: `ari:cloud:jira::site/${cloudId}`,
		showFlag: addFlag,
		source: 'peopleMenu',
		subProduct,
		experimental_applicationEdition: software,
		navigation: { directoryContextEntryPoint: '/jira' },
		onSubmit: async (data: OnSubmitData) => {
			await data.defaultOnSubmit();
		},
		experimental_featuredUsers: fetchInviteRecommendations,
	});

	return invitePeopleModalTrigger;
};

const getFlagComponent = (appearance: Flag['appearance']) => {
	switch (appearance) {
		case 'warning':
			return WarningFlag;
		case 'error':
			return ErrorFlag;
		case 'success':
		case 'normal':
		default:
			return SuccessFlag;
	}
};
